import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error(language === 'it' ? 'Inserisci un email valida' : 'Enter a valid email');
      return;
    }

    setSubscribing(true);

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'newsletter' })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(language === 'it' 
          ? '✅ Iscrizione completata! Controlla la tua email per il coupon 5%' 
          : '✅ Subscribed! Check your email for the 5% coupon');
        setEmail('');
      } else {
        toast.error(data.message || (language === 'it' ? 'Errore iscrizione' : 'Subscription error'));
      }
    } catch (error) {
      toast.error(language === 'it' ? 'Errore del server' : 'Server error');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-black border-t border-white/25 mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div>
            <img 
              src="/allmx-logo.png" 
              alt="ALLMX" 
              className="h-8 md:h-10 w-auto object-contain mb-4"
              style={{ 
                filter: 'hue-rotate(18deg) saturate(1.5) brightness(1.1)'
              }}
            />
            <p className="text-white/85 text-base leading-relaxed mb-6">
              {t.footer.description}
            </p>
            <div className="flex gap-4">
              <a href="https://wa.me/393711289838" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#25D366] transition-colors" title="WhatsApp" aria-label="WhatsApp">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a href="mailto:info@allmx.it" className="text-white/60 hover:text-[var(--brand-primary)] transition-colors" title="Email" aria-label="Email">
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/products" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {t.nav.products}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link to="/about#video" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors" data-testid="footer-promo-video-link">
                  {{ it: 'Video promo', en: 'Promo video', es: 'Vídeo promo' }[language] || 'Video promo'}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {t.nav.blog}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {t.nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Informazioni Legali */}
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">
              {language === 'it' ? 'Informazioni Legali' : 'Legal Information'}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-and-conditions" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {language === 'it' ? 'Termini e Condizioni' : 'Terms & Conditions'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">{t.footer.shop}</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.amazon.it/s?me=A3526ZZLQNVU1W&marketplaceID=APJ6JRA9NG5V4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t.footer.amazonStore}
                </a>
              </li>
              <li>
                <a
                  href="https://www.ebay.it/str/allmx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t.footer.ebayStore}
                </a>
              </li>
              <li>
                <Link to="/where-to-buy" className="text-white/70 hover:text-[var(--brand-primary)] transition-colors">
                  {t.nav.whereToBuy}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white text-lg font-semibold mb-4">{t.footer.newsletter}</h3>
            <p className="text-white/70 text-sm mb-3">
              {t.footer.newsletterDesc}
            </p>
            {/* Coupon Incentive */}
            <div className="bg-gradient-to-r from-orange-500/20 to-orange-600/20 border-l-4 border-[var(--brand-primary)] p-3 mb-4">
              <p className="text-[var(--brand-primary)] font-bold text-base mb-1">
                🎁 {t.footer.couponTitle}
              </p>
              <p className="text-white/80 text-sm">
                {t.footer.couponDesc}
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.emailPlaceholder}
                className="px-4 py-3 bg-white/10 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-[var(--brand-primary)]"
                style={{ borderRadius: '0px' }}
                disabled={subscribing}
              />
              <button 
                type="submit" 
                className="btn-primary"
                disabled={subscribing}
              >
                {subscribing ? (language === 'it' ? 'Invio...' : 'Sending...') : t.footer.subscribe}
              </button>
            </form>
          </div>
        </div>

        {/* Mappa Google Maps */}
        <div className="mt-12 mb-8">
          <h3 className="text-white text-xl font-semibold mb-4 text-center">
            {language === 'it' ? 'Dove Siamo' : 'Where We Are'}
          </h3>
          <div className="w-full rounded-lg overflow-hidden border border-white/20" style={{ height: '300px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3149.8441776890373!2d13.341858776561895!3d38.11953797189874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1319ef5d5d5d5d5d%3A0x1234567890abcdef!2sVia%20Umberto%20Giordano%2C%2084%2C%2090144%20Palermo%20PA!5e0!3m2!1sit!2sit!4v1234567890123!5m2!1sit!2sit"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ala S.r.l. - Via Umberto Giordano, 84, Palermo"
            ></iframe>
          </div>
          <p className="text-center text-white/60 text-sm mt-3">
            <strong className="text-white">Ala S.r.l.</strong> - Via Umberto Giordano, 84 - 90144 Palermo (PA)
          </p>
        </div>

        {/* Dati Aziendali - Trasparenza Google Merchant Center */}
        <div className="border-t border-white/15 pt-8 mb-8">
          <h3 className="text-white text-base font-semibold mb-4 text-center">
            {language === 'it' ? 'Dati Aziendali' : language === 'es' ? 'Datos de la Empresa' : 'Company Information'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-white/70 max-w-4xl mx-auto">
            <p><span className="text-white/50">{language === 'it' ? 'Ragione Sociale:' : language === 'es' ? 'Razón Social:' : 'Company Name:'}</span> <strong className="text-white">Ala S.r.l.</strong></p>
            <p><span className="text-white/50">{language === 'it' ? 'Sede Legale:' : language === 'es' ? 'Sede Legal:' : 'Registered Office:'}</span> Via Umberto Giordano, 84 — 90144 Palermo (PA), {language === 'it' ? 'Italia' : language === 'es' ? 'Italia' : 'Italy'}</p>
            <p><span className="text-white/50">P.IVA / C.F.:</span> 02988930828</p>
            <p><span className="text-white/50">REA:</span> PA-120943</p>
            <p><span className="text-white/50">{language === 'it' ? 'Capitale Sociale:' : language === 'es' ? 'Capital Social:' : 'Share Capital:'}</span> € 10.400,00 i.v.</p>
            <p><span className="text-white/50">{language === 'it' ? 'Codice SDI:' : language === 'es' ? 'Código SDI:' : 'SDI Code:'}</span> QULXG4S</p>
            <p><span className="text-white/50">{language === 'it' ? 'Telefono:' : language === 'es' ? 'Teléfono:' : 'Phone:'}</span> <a href="tel:+39091226476" className="hover:text-[var(--brand-primary)]">+39 091 226476</a></p>
            <p><span className="text-white/50">Email:</span> <a href="mailto:info@allmx.it" className="hover:text-[var(--brand-primary)]">info@allmx.it</a></p>
            <p className="md:col-span-2"><span className="text-white/50">PEC:</span> <a href="mailto:alasrlpalermo@pec.it" className="hover:text-[var(--brand-primary)]">alasrlpalermo@pec.it</a></p>
          </div>
        </div>

        <div className="border-t border-white/25 pt-8 text-center text-white/60 text-sm">
          <p>{t.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
